/**
 * Auth flow observability — thin wrappers over generic flow tracing.
 *
 * Keeps auth-specific step names and param summarizers here; delegates
 * breadcrumbs, spans, and failure capture to @be-miniapp/logging.
 */

import {
  recordFlowOutcome,
  recordFlowStep,
  recordTrackedFailure,
  runFlowSpan,
  runInstrumentedCall,
  scrubValue,
} from "@be-miniapp/logging"

import type { AuthErrorCode } from "@/constants/auth-error"
import type { AuthenticationParams } from "./auth-types"

const AUTH_FEATURE = "auth"
const AUTH_CATEGORY = "auth"

export type AuthStep =
  | "init.start"
  | "init.params_parsed"
  | "init.partner_configured"
  | "init.waiting_for_params"
  | "init.dev_auto_restore"
  | "init.provider_error"
  | "init.persisted_restore"
  | "init.persisted_invalid"
  | "init.token_exchange"
  | "init.session_persisted"
  | "init.location_fetch"
  | "init.complete"
  | "init.missing_params_timeout"
  | "api.parse_params"
  | "api.get_partner_code"
  | "api.exchange_access_token"
  | "api.authenticate_from_params"

type AuthFailureContext = {
  step: AuthStep
  code?: AuthErrorCode | string
  strategy?: AuthenticationParams["kind"]
  extra?: Record<string, unknown>
}

function maskValue(value: string | null | undefined, visible = 4): string | undefined {
  if (!value) return undefined
  if (value.length <= visible) return "*".repeat(value.length)
  return `${value.slice(0, visible)}…(${value.length})`
}

export function summarizeAuthParams(
  authParams: AuthenticationParams
): Record<string, unknown> {
  switch (authParams.kind) {
    case "oauth":
      return {
        kind: authParams.kind,
        hasCode: Boolean(authParams.params.code),
        hasCodeVerifier: Boolean(authParams.params.codeVerifier),
        codePrefix: maskValue(authParams.params.code),
      }
    case "internal-testing":
      return {
        kind: authParams.kind,
        partnerId: authParams.params.partnerId,
        phoneSuffix: maskValue(authParams.params.phoneNo, 3),
        fullname: authParams.params.fullname,
        hasPartnerSecret: Boolean(authParams.params.partnerSecret),
      }
    case "provider-error":
      return {
        kind: authParams.kind,
        error: authParams.params.error,
        description: authParams.params.description,
      }
    case "persisted":
      return { kind: authParams.kind }
    default:
      return { kind: authParams.kind }
  }
}

export function summarizeSearchParams(searchParams: URLSearchParams) {
  const keys = Array.from(new Set(searchParams.keys())).sort()
  return {
    paramKeys: keys.join(","),
    hasCode: searchParams.has("code"),
    hasCodeVerifier: searchParams.has("code_verifier"),
    hasPhoneNo: searchParams.has("phone_no"),
    hasPersisted: searchParams.get("persisted") === "true",
    hasProviderError: searchParams.has("error"),
    hasLocation:
      searchParams.has("latitude") && searchParams.has("longitude"),
    partner: searchParams.get("ptr") ?? "unknown",
  }
}

export function recordAuthStep(
  step: AuthStep,
  data?: Record<string, unknown>,
  level: "info" | "warning" | "error" = "info"
) {
  recordFlowStep({
    feature: AUTH_FEATURE,
    category: AUTH_CATEGORY,
    step,
    data,
    level,
  })
}

export function recordAuthOutcome(
  outcome: "success" | "fail",
  extra?: Record<string, unknown>
) {
  const scrubbedExtra = scrubValue(extra) as Record<string, unknown> | undefined

  recordFlowOutcome(
    AUTH_FEATURE,
    outcome === "success" ? "init.complete" : "init.token_exchange",
    outcome,
    scrubbedExtra
  )
}

export function recordAuthFailure(
  error: unknown,
  context: AuthFailureContext
) {
  recordTrackedFailure({
    error,
    feature: AUTH_FEATURE,
    step: context.step,
    tags: {
      error_code: context.code ?? "unknown",
    },
    extra: {
      ...context.extra,
      strategy: context.strategy,
    },
  })
}

export async function runAuthInitializationSpan<T>(
  attributes: Record<string, string | number | boolean>,
  callback: () => Promise<T>
): Promise<T> {
  return runFlowSpan(
    {
      name: "appview.auth.initialize",
      op: "auth.init",
      feature: AUTH_FEATURE,
      forceTransaction: true,
      attributes: {
        platform: "appview",
        ...attributes,
      },
    },
    callback
  )
}

export async function runAuthApiSpan<T>(
  step: AuthStep,
  attributes: Record<string, string | number | boolean>,
  callback: () => Promise<T>
): Promise<T> {
  return runInstrumentedCall(
    {
      feature: AUTH_FEATURE,
      step,
      spanName: `appview.auth.${step}`,
      op: "auth.api",
      attributes: {
        platform: "appview",
        ...attributes,
      },
    },
    callback
  )
}
